import React, { useState } from 'react';
import Icon from './Icon';

interface PhotoSliderProps {
  images: string[];
  baseUrl?: string;
}

const BASE = '';

const PhotoSlider: React.FC<PhotoSliderProps> = ({ images, baseUrl = BASE }) => {
  const [idx, setIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  if (!images.length) return null;

  const src = (path: string) => path.startsWith('http') ? path : `${baseUrl}${path}`;

  const prev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIdx(i => (i - 1 + images.length) % images.length);
  };
  const next = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIdx(i => (i + 1) % images.length);
  };

  return (
    <>
      {/* Слайдер */}
      <div style={{ position: 'relative', width: '100%', marginBottom: 20 }}>
        {/* Фото */}
        <div
          onClick={() => setFullscreen(true)}
          style={{
            width: '100%',
            borderRadius: 'var(--r-md)',
            overflow: 'hidden',
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            cursor: 'zoom-in',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={src(images[idx])}
            alt={`Фото ${idx + 1}`}
            style={{
              width: '100%',
              maxHeight: 280,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </div>

        {/* Стрелки (только если фото > 1) */}
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              style={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: '1px solid var(--glass-border)',
                background: 'rgba(13,17,23,0.7)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="chevron_left" size={16} color="var(--text0)" />
            </button>
            <button
              onClick={next}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: '1px solid var(--glass-border)',
                background: 'rgba(13,17,23,0.7)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="chevron_right" size={16} color="var(--text0)" />
            </button>

            {/* Счётчик */}
            <div style={{
              position: 'absolute',
              bottom: 8,
              right: 10,
              background: 'rgba(13,17,23,0.7)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              borderRadius: 20,
              padding: '3px 10px',
              fontSize: 12,
              fontFamily: 'var(--mono)',
              color: 'var(--text1)',
            }}>
              {idx + 1}/{images.length}
            </div>

            {/* Точки */}
            <div style={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 5,
            }}>
              {images.map((_, i) => (
                <div
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  style={{
                    width: i === idx ? 16 : 6,
                    height: 6,
                    borderRadius: 3,
                    background: i === idx ? 'var(--teal-solid)' : 'rgba(255,255,255,0.35)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Полноэкранный просмотр */}
      {fullscreen && (
        <div
          onClick={() => setFullscreen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <img
            src={src(images[idx])}
            alt={`Фото ${idx + 1}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: 'var(--r-md)',
            }}
          />
          {images.length > 1 && (
            <>
              <button
                onClick={prev}
                style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  width: 40, height: 40, borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.1)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Icon name="chevron_left" size={20} color="#fff" />
              </button>
              <button
                onClick={next}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  width: 40, height: 40, borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.1)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Icon name="chevron_right" size={20} color="#fff" />
              </button>
            </>
          )}
          <div style={{
            position: 'absolute', top: 16, right: 16,
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}>
            <Icon name="close" size={18} color="#fff" />
          </div>
          {images.length > 1 && (
            <div style={{
              position: 'absolute', bottom: 20,
              fontFamily: 'var(--mono)', color: 'rgba(255,255,255,0.6)', fontSize: 13,
            }}>
              {idx + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PhotoSlider;
